import { Box, Typography } from "@mui/material"
import { CheckCircleFilledRounded } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React from "react"
import { useFormContext } from "react-hook-form"

import { useGetContactGeneralInformation } from "../../hooks/useGetContactCompany"

import ContactMethodFormFields from "@/components/Contact/ContactMethodFormFields"
import { type Company, type Location } from "@/graphql/codegen/graphql"
import type { ContactAddFormValues } from "@/schemas/contactFormSchema"
import { formatLocationAddress } from "@/utils/utils"

export interface AddContactMethodsFormProps {
  companyId: string
}

export const AddContactMethodsForm: FC<AddContactMethodsFormProps> = ({ companyId }) => {
  const { getValues } = useFormContext<ContactAddFormValues>()
  const formValues = getValues()
  const {
    company,
    location,
    fetching: contactGeneralInformationFetching,
  } = useGetContactGeneralInformation({
    companyId,
    locationId: formValues.physicalLocationId ?? undefined,
  })

  return (
    <form className='space-y-6'>
      <ContactGeneralInfo
        formValues={formValues}
        company={company}
        location={location}
        loading={contactGeneralInformationFetching}
      />
      <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
        2. Contact methods
      </Typography>
      <ContactMethodFormFields />
    </form>
  )
}

const GeneralInfoText: FC<{ children?: React.ReactNode }> = ({ children }) => (
  <p className='py-0 text-sm text-gray-900'>{children ?? <>&nbsp;</>}</p>
)

interface ContactGeneralInfoProps {
  formValues: ContactAddFormValues
  company?: Company
  location?: Location
  loading?: boolean
}

const ContactGeneralInfo: FC<ContactGeneralInfoProps> = ({ formValues, company, location, loading }) => {
  return (
    <Box className='space-y-4'>
      <Box className='mb-2' display='flex' alignItems='center'>
        <Typography
          variant='body1'
          fontWeight={700}
          className='leading-6 tracking-[0.15px] text-gray-700'
          display='flex'
          alignItems='center'
        >
          1. General Information
          <CheckCircleFilledRounded className='ml-1 text-success-500' />
        </Typography>
      </Box>
      <Box className='px-10'>
        {loading ? (
          <>
            <GeneralInfoText>
              <em>Loading...</em>
            </GeneralInfoText>
            {/* DOM placeholders to prevent "jumping" when transitioning between loading states */}
            <GeneralInfoText />
            <GeneralInfoText />
            <GeneralInfoText />
            <GeneralInfoText />
            <GeneralInfoText />
          </>
        ) : (
          <>
            <GeneralInfoText>First Name: {formValues.firstName}</GeneralInfoText>
            <GeneralInfoText>Last Name: {formValues.lastName}</GeneralInfoText>
            <GeneralInfoText>Company: {company?.name ?? "-"}</GeneralInfoText>
            {formValues.companyDepartment && (
              <GeneralInfoText>Company Department: {formValues.companyDepartment}</GeneralInfoText>
            )}
            {formValues.title && <GeneralInfoText>Title: {formValues.title}</GeneralInfoText>}
            {location && (
              <GeneralInfoText>Office Location: {location ? formatLocationAddress(location) : "-"}</GeneralInfoText>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default AddContactMethodsForm
